import { GuestPreferenceStatModel } from 'api-hooks/guest';
import { Option, Value } from 'baseui/select';
import { capitalize } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { addNewGuest } from '../../hooks/use-get-default-values';
import { DocumentSaleGuestTypeFormType } from '../form-type';

function tagTransformer(item: GuestPreferenceStatModel): Option {
  return {
    id: item.id,
    label: `${item.name} ${item.count > 1 ? `(X${item.count})` : ''}`,
  };
}

type DocumentSaleStatusType = {
  isSubmitted: boolean;
  isProccessed: boolean;
  isFinished: boolean;
};
interface Props {
  parentIndex: number;
  documentType: string;
  documentSaleStatus: DocumentSaleStatusType;
}

function useGetGuestTableList(val: DocumentSaleStatusType): IColumn<any>[] {
  const { isFinished, isSubmitted } = val;
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      disabled: isFinished || isSubmitted,
      selectProps: guestColumn,
    },
    ...(editable && !(isSubmitted || isFinished)
      ? [
          {
            header: '',
            accessorKey: 'delete',
            minSize: 60,
            size: 60,
            maxSize: 60,
            dataType: ColumnDataType.delete,
          },
        ]
      : []),
  ];
}

export default function GuestTable(props: Props) {
  const {
    documentSaleStatus,
    // documentType
  } = props;
  const name = `documentSales.${props.parentIndex}.documentSaleGuests` as const;
  const { editable } = useFormState();

  const { setValue, getValues, control, trigger, clearErrors } =
    useFormContext();

  const fields = useWatch({
    name,
  });
  const { remove, update } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const columns = useGetGuestTableList(documentSaleStatus);

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentActionRow = action.rowIndex as number;
      const guest = guests[currentActionRow];

      const type = action.type as ActionType;
      const columnId = action.columnId as keyof DocumentSaleGuestTypeFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<DocumentSaleGuestTypeFormType> = () => {
        if (columnId === 'guestId') {
          return {
            guestPreferences:
              extra?.[0]?.extra.preferenceStats.map((item) =>
                tagTransformer(item),
              ) || [],
            guestName: extra?.[0]
              ? `${capitalize(extra?.[0]?.extra?.title)} ${
                  extra?.[0].extra.name
                }`
              : '',
          };
        }
        return {};
      };

      if (type === 'addRow') {
        const addRow: DocumentSaleGuestTypeFormType[] = [
          ...guests,
          addNewGuest(),
        ];
        setValue(name, addRow);
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentActionRow);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentActionRow, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentActionRow}`);
      }
    },
    [clearErrors, getValues, name, remove, setValue, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
        noAddRemoveRow={
          documentSaleStatus.isSubmitted || documentSaleStatus.isFinished
        }
      />
    </div>
  );
}
